/**
 * 30-day expiry notices — one row per title on the Renewals page.
 *
 * Emails members whose membership will not renew automatically, 30 days
 * before it ends. Each title has its own switch (off until turned on), a
 * preview of who is inside the window, and a test send that goes only to
 * the title's test recipients.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api/client'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { apiErrorText } from '../lib/apiErrorText'
import { fmtInt } from '../lib/format'

type Title = {
  publication_slug: string
  publication_name: string
  enabled: boolean
  enabled_on: string | null
  catch_up: boolean
  template_id: number | null
  test_recipients: string | null
  tag_open: boolean
  due_today: number
  next_30_days: number
  /** Turned on today without catch-up: members reaching exactly 30 days. */
  would_send_today_if_on: number
  /** What catch-up adds: inside the window already, not yet notified. */
  catch_up_extra: number
  /** The 15-day, 5-day and end-date follow-ups. */
  followups_enabled: boolean
  followups_enabled_on: string | null
  /** Members on a follow-up step today who have not had it yet. */
  followups_waiting: number
  /** step ("15" | "5" | "0") -> template id */
  followup_template_ids: Record<string, number>
}

type PreviewRow = {
  subscription_id: number
  subscriber_id: number
  email: string
  period_end: string
  reason: string
  is_gift: boolean
  already_sent: boolean
  member_name: string
  would_send_today: boolean
  step: number
}

type TestResult = { sent: number; to: string[]; failed: { to: string; error: string }[] }

const REASON: Record<string, string> = { auto_renew_off: 'Auto-renew off', no_card: 'No card on file' }
const STEPS = [30, 15, 5, 0] as const
const stepLabel = (n: number) => (n === 0 ? 'End date' : `${n} days`)
const INPUT = 'bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono text-[12px] text-paper focus:outline-none focus:border-amber-glow disabled:opacity-60'

export function ExpiryNotices() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['expiry-notices'],
    queryFn: () => api.get<Title[]>('/api/renewals/expiry-notices/'),
  })
  return (
    <section className="space-y-3">
      <div>
        <h2 className="label">30-day expiry notices</h2>
        <p className="mt-1 text-sm text-paper-dim">
          Emails members whose membership won&rsquo;t renew automatically, 30 days before it ends.
        </p>
      </div>
      {isLoading && <p className="label">Loading…</p>}
      {isError && <p className="text-radar-red font-mono text-[12px]">Couldn&rsquo;t load the expiry notices.</p>}
      {data && (
        <div className="border border-ink-500 divide-y divide-ink-500">
          {data.map((t) => <TitleRow key={t.publication_slug} t={t} />)}
        </div>
      )}
    </section>
  )
}

function TitleRow({ t }: { t: Title }) {
  const qc = useQueryClient()
  const [turnOn, setTurnOn] = useState(false)
  const [turnOff, setTurnOff] = useState(false)
  const [showList, setShowList] = useState(false)
  const [recipients, setRecipients] = useState<string | null>(null)
  const [catchUp, setCatchUp] = useState(false)
  const [followups, setFollowups] = useState<'on' | 'off' | null>(null)

  const save = useMutation({
    mutationFn: (body: { enabled?: boolean; catch_up?: boolean; test_recipients?: string; followups_enabled?: boolean }) =>
      api.put(`/api/renewals/expiry-notices/${t.publication_slug}`, body),
    onSuccess: () => {
      setTurnOn(false)
      setTurnOff(false)
      setFollowups(null)
      setRecipients(null)
      qc.invalidateQueries({ queryKey: ['expiry-notices'] })
      qc.invalidateQueries({ queryKey: ['expiry-notice-preview', t.publication_slug] })
    },
  })

  const edited = recipients !== null && recipients.trim() !== (t.test_recipients ?? '')

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <PubBadge slug={t.publication_slug} />
        <span
          className={`font-mono text-[11px] uppercase tracking-label ${t.enabled ? 'text-radar-green' : 'text-paper-trace'}`}
        >
          {t.enabled ? 'On' : 'Off'}
          {t.enabled && t.enabled_on ? ` since ${t.enabled_on}` : ''}
        </span>
        {t.enabled ? (
          <span className="font-mono text-[12px] text-paper-dim">
            <span className="text-paper">{fmtInt(t.due_today)}</span> due today
          </span>
        ) : (
          <span className="font-mono text-[12px] text-paper-dim">
            Would send today if on: <span className="text-paper">{fmtInt(t.would_send_today_if_on)}</span>
          </span>
        )}
        <span className="font-mono text-[12px] text-paper-dim">
          <span className="text-paper">{fmtInt(t.next_30_days)}</span> in the next 30 days
        </span>
        {t.template_id != null && (
          <Link to={`/templates/${t.template_id}`} className="font-mono text-[12px] text-amber-glow hover:underline">
            Edit wording
          </Link>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowList((v) => !v)}>
            {showList ? 'Hide list' : 'Show list'}
          </Button>
          {t.enabled ? (
            <Button variant="outline" size="sm" onClick={() => { save.reset(); setTurnOff(true) }}>Turn off</Button>
          ) : (
            <Button size="sm" onClick={() => { save.reset(); setCatchUp(false); setTurnOn(true) }}>Turn on</Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pl-1">
        <span className="label">Follow-ups · 15 days, 5 days, end date</span>
        <span
          className={`font-mono text-[11px] uppercase tracking-label ${t.followups_enabled ? 'text-radar-green' : 'text-paper-trace'}`}
        >
          {t.followups_enabled ? 'On' : 'Off'}
          {t.followups_enabled && t.followups_enabled_on ? ` since ${t.followups_enabled_on}` : ''}
        </span>
        <span className="font-mono text-[12px] text-paper-dim">
          <span className="text-paper">{fmtInt(t.followups_waiting)}</span>{' '}
          {t.followups_enabled ? 'due a follow-up' : 'would get one on the next run if on'}
        </span>
        {STEPS.filter((n) => n !== 30).map((n) => {
          const id = t.followup_template_ids?.[String(n)]
          return id != null ? (
            <Link key={n} to={`/templates/${id}`} className="font-mono text-[12px] text-amber-glow hover:underline">
              Edit {stepLabel(n).toLowerCase()} wording
            </Link>
          ) : null
        })}
        <div className="ml-auto">
          {t.followups_enabled ? (
            <Button variant="outline" size="sm" onClick={() => { save.reset(); setFollowups('off') }}>Turn off follow-ups</Button>
          ) : (
            <Button size="sm" onClick={() => { save.reset(); setFollowups('on') }}>Turn on follow-ups</Button>
          )}
        </div>
      </div>

      {!t.tag_open && (
        <p className="font-mono text-[11px] text-amber-glow">
          Real sends are blocked on this server until the expiry-notice mail tag is opened. Test emails still send.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 min-w-[320px]">
          <span className="label">Test recipients</span>
          <input
            className={INPUT}
            value={recipients ?? t.test_recipients ?? ''}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="name@example.com, other@example.com"
          />
        </label>
        <Button
          variant="outline"
          size="sm"
          disabled={!edited || save.isPending}
          onClick={() => save.mutate({ test_recipients: (recipients ?? '').trim() })}
        >
          {save.isPending && !turnOn && !turnOff ? 'Saving…' : 'Save recipients'}
        </Button>
      </div>

      {save.error && !turnOn && !turnOff && (
        <p className="font-mono text-[11px] text-radar-red">{saveError(save.error)}</p>
      )}

      {showList && <PreviewList slug={t.publication_slug} />}

      {turnOn && (
        <Modal onClose={() => setTurnOn(false)}>
          <div className="label-amber">Turn on 30-day notices · {t.publication_name}</div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" className="mt-1" checked={!catchUp} onChange={() => setCatchUp(false)} />
            <span className="text-sm text-paper">Only members who reach 30 days from today on</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" className="mt-1" checked={catchUp} onChange={() => setCatchUp(true)} />
            <span className="text-sm text-paper">
              Also everyone already inside 30 days ({fmtInt(t.catch_up_extra)} members)
            </span>
          </label>
          {save.error && <p className="font-mono text-[11px] text-radar-red">{saveError(save.error)}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" disabled={save.isPending} onClick={() => setTurnOn(false)}>Cancel</Button>
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ enabled: true, catch_up: catchUp })}>
              {save.isPending ? 'Turning on…' : 'Turn on'}
            </Button>
          </div>
        </Modal>
      )}

      {followups && (
        <Modal onClose={() => setFollowups(null)}>
          <div className="label-amber">
            Turn {followups} follow-up emails · {t.publication_name}
          </div>
          {followups === 'on' ? (
            <p className="text-sm text-paper">
              Members who haven&rsquo;t renewed will also get an email 15 and 5 days before their end date, and on the
              end date. On the next run (12:00 UTC), {fmtInt(t.followups_waiting)} members already inside 15 days get
              the email for the step they&rsquo;re on.
              {!t.enabled && ' The 30-day notices for this title are off, so nothing sends until you turn them on too.'}
            </p>
          ) : (
            <p className="text-sm text-paper">
              Only the 30-day email will be sent for this title until you turn follow-ups on again.
            </p>
          )}
          {save.error && <p className="font-mono text-[11px] text-radar-red">{saveError(save.error)}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" disabled={save.isPending} onClick={() => setFollowups(null)}>Cancel</Button>
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ followups_enabled: followups === 'on' })}>
              {save.isPending ? 'Saving…' : followups === 'on' ? 'Turn on follow-ups' : 'Turn off follow-ups'}
            </Button>
          </div>
        </Modal>
      )}

      {turnOff && (
        <Modal onClose={() => setTurnOff(false)}>
          <div className="label-amber">Turn off 30-day notices · {t.publication_name}</div>
          <p className="text-sm text-paper">
            No more expiry notices will be sent for this title until you turn it on again.
          </p>
          {save.error && <p className="font-mono text-[11px] text-radar-red">{saveError(save.error)}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" disabled={save.isPending} onClick={() => setTurnOff(false)}>Cancel</Button>
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ enabled: false })}>
              {save.isPending ? 'Turning off…' : 'Turn off'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function saveError(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) return 'Only an admin can change these settings.'
  return apiErrorText(err)
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-500 w-full max-w-[520px] p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

function PreviewList({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['expiry-notice-preview', slug],
    queryFn: () => api.get<{ rows: PreviewRow[] }>(`/api/renewals/expiry-notices/${slug}/preview?days=30`),
  })
  const [results, setResults] = useState<Record<number, { ok: boolean; text: string }>>({})
  const [testStep, setTestStep] = useState<Record<number, number>>({})
  const test = useMutation({
    mutationFn: ({ id, step }: { id: number; step: number }) =>
      api.post<TestResult>(`/api/renewals/expiry-notices/${slug}/test`, { subscription_id: id, step }),
    onSuccess: (r, { id }) => {
      const bad = r.failed.length
        ? `. Could not send to ${r.failed.map((f) => `${f.to} (${f.error})`).join('; ')}`
        : ''
      setResults((p) => ({ ...p, [id]: { ok: !bad, text: `Test sent to ${r.to.filter((a) => !r.failed.some((f) => f.to === a)).join(', ')}${bad}` } }))
    },
    onError: (e, { id }) => {
      setResults((p) => ({ ...p, [id]: { ok: false, text: saveError(e) } }))
    },
  })

  if (q.isLoading) return <p className="label">Loading…</p>
  if (q.isError) return <p className="text-radar-red font-mono text-[12px]">Couldn&rsquo;t load the list.</p>
  const rows = q.data?.rows ?? []
  if (rows.length === 0) return <p className="text-sm text-paper-dim">No member is inside the 30-day window.</p>
  return (
    <div className="border border-ink-500 overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-ink-500 text-left label">
            <th className="px-3 py-2">Member</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Ends</th>
            <th className="px-3 py-2">Gift</th>
            <th className="px-3 py-2">Reason</th>
            <th className="px-3 py-2">Step today</th>
            <th className="px-3 py-2">Already sent</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const res = results[r.subscription_id]
            return (
              <tr key={r.subscription_id} className="border-b border-ink-700/40 align-top">
                <td className="px-3 py-2 text-sm">
                  <Link to={`/subscribers/${r.subscriber_id}`} className="text-paper hover:text-amber-glow hover:underline">
                    {r.member_name || `#${r.subscriber_id}`}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-[12px] text-paper-dim">{r.email}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{r.period_end}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{r.is_gift ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{REASON[r.reason] ?? r.reason}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{stepLabel(r.step)}</td>
                <td className="px-3 py-2 font-mono text-[12px]">{r.already_sent ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <select
                    aria-label="Which email to test"
                    className={`${INPUT} mr-2`}
                    value={testStep[r.subscription_id] ?? r.step}
                    onChange={(e) => setTestStep((p) => ({ ...p, [r.subscription_id]: Number(e.target.value) }))}
                  >
                    {STEPS.map((n) => <option key={n} value={n}>{stepLabel(n)}</option>)}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={test.isPending && test.variables?.id === r.subscription_id}
                    onClick={() => test.mutate({ id: r.subscription_id, step: testStep[r.subscription_id] ?? r.step })}
                  >
                    {test.isPending && test.variables?.id === r.subscription_id ? 'Sending…' : 'Send test'}
                  </Button>
                  {res && (
                    <div className={`mt-1 max-w-[320px] font-mono text-[11px] ${res.ok ? 'text-radar-green' : 'text-radar-red'}`}>
                      {res.text}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
